'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { useComposerPlusIcon } from '#pplx/frontend/composer-sdk/components/ComposerPlusIcon.js';
import { useComposerEditor } from '#pplx/frontend/composer-sdk/ComposerEditorContext.js';
import { PluginContextProvider } from '#pplx/frontend/composer-sdk/internal/plugins/pluginContext.js';
import { usePlusMenuItems } from '#pplx/frontend/composer-sdk/internal/plus-menu/usePlusMenuItems.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
/** Renders the composer plus-menu trigger and every registered plus-menu item. */
export function PlusMenuCapabilityHost({ plugins, placement = 'below', disabled = false, }) {
    const { $t } = useIntl();
    const { isMobileStyle } = useAetherContext();
    const { focus } = useComposerEditor();
    const [isOpen, setIsOpen] = useState(false);
    const isOpenRef = useRef(false);
    const { icon: plusIcon, closeLabel } = useComposerPlusIcon(isOpen);
    const handleOpenChange = useCallback((open) => {
        if (isOpenRef.current === open) {
            return;
        }
        // Deduplicate same-gesture close requests without running observers inside
        // a state updater, which React may replay.
        isOpenRef.current = open;
        setIsOpen(open);
        for (const plugin of plugins) {
            for (const onOpenChange of plugin.plusMenuOpenChange ?? EMPTY_ARRAY) {
                onOpenChange(open, plugin.props ?? {});
            }
        }
        if (!open && !isMobileStyle) {
            // Focus before teardown so an outside click can still focus its target.
            // Non-modal Radix treats editor focus as an outside interaction and skips
            // trigger restoration. Mobile sheets retain their default trigger focus.
            focus();
        }
    }, [focus, isMobileStyle, plugins]);
    useEffect(() => {
        if (disabled) {
            handleOpenChange(false);
        }
    }, [disabled, handleOpenChange]);
    const items = usePlusMenuItems(plugins, isOpen);
    const hasRegisteredItems = useMemo(() => plugins.some((plugin) => (plugin.plusMenu?.length ?? 0) > 0), [plugins]);
    const closeMenu = useCallback(() => {
        handleOpenChange(false);
    }, [handleOpenChange]);
    const addLabel = $t({
        defaultMessage: 'Add',
        description: 'Aria label for the composer plus menu trigger button',
        id: 'fc4a9d0091',
    });
    const boundItems = useMemo(() => items.map((item) => ({
        key: `${item.pluginId}:${item.contribution.id}:${item.registrationIndex}`,
        contribution: item.contribution,
        context: { pluginId: item.pluginId },
        props: item.props ?? {},
    })), [items]);
    if (!hasRegisteredItems) {
        return null;
    }
    return (_jsx(DropdownMenu, { isOpen: isOpen && !disabled, onToggle: handleOpenChange, side: placement === 'above' ? 'top' : 'bottom', triggerElement: _jsx(Button, { rounded: true, icon: plusIcon, "aria-label": closeLabel ?? addLabel, disabled: disabled, size: "small", variant: "text", track: false }), mobileBottomSheetTitle: addLabel, children: boundItems.map(({ key, contribution, context, props }) => (_jsx(PluginContextProvider, { value: context, children: contribution.render({ isOpen, closeMenu, props }) }, key))) }));
}
//# sourceMappingURL=PlusMenuCapabilityHost.js.map